import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Workflow, CalendarClock } from 'lucide-react';
import { format } from 'date-fns';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type {
  ApplyWorkflowResponse,
  EventTeamMember,
  EventWorkflow,
  EventWorkflowWithStages,
} from '../../types/eventTeam';

export interface ApplyWorkflowModalProps {
  isOpen: boolean;
  eventId: string;
  /** Event's run-window start — stage `offset_days` are relative to this (guide §1.9). */
  scheduledStart: number | null;
  onClose: () => void;
  onSuccess: (result: ApplyWorkflowResponse) => void;
}

export const DAY_MS = 86_400_000;

/** `task.due_date = scheduled_start + offset_days · 86400000` (negative = days before). */
export function computeStageDueDate(scheduledStart: number | null, offsetDays: number | null): number | null {
  if (scheduledStart == null || offsetDays == null) return null;
  return scheduledStart + offsetDays * DAY_MS;
}

/**
 * Apply a workflow template to an event, instantiating one due-dated task per stage.
 * Shows a preview of computed due dates before applying (guide §1.9).
 */
export function ApplyWorkflowModal({ isOpen, eventId, scheduledStart, onClose, onSuccess }: ApplyWorkflowModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [workflows, setWorkflows] = useState<EventWorkflow[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [selected, setSelected] = useState<EventWorkflowWithStages | null>(null);
  const [members, setMembers] = useState<EventTeamMember[]>([]);
  const [assigneeUuid, setAssigneeUuid] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId('');
    setSelected(null);
    setAssigneeUuid('');
    setError(null);
    setLoading(true);
    Promise.allSettled([
      eventTeamApiService.listWorkflows(true),
      eventTeamApiService.listTeamMembers({ status: 'active', limit: 200 }),
    ]).then(([wfRes, memberRes]) => {
      if (wfRes.status === 'fulfilled') setWorkflows(wfRes.value);
      else setError(wfRes.reason instanceof Error ? wfRes.reason.message : 'Failed to load workflows');
      if (memberRes.status === 'fulfilled') setMembers(memberRes.value.results);
      setLoading(false);
    });
  }, [isOpen]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    eventTeamApiService
      .getWorkflow(selectedId)
      .then((wf) => {
        if (!cancelled) setSelected(wf);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load workflow stages');
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const handleApply = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await eventTeamApiService.applyWorkflow(eventId, selectedId, assigneeUuid || undefined);
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply workflow');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const orderedStages = selected ? [...selected.stages].sort((a, b) => a.position - b.position) : [];

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4 flex items-center gap-2">
          <Workflow className="w-6 h-6 text-accent-400" />
          Apply workflow
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-6 h-6 animate-spin text-accent-400" />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Workflow template</label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="glass-input w-full text-sm"
              >
                <option value="">Select a workflow…</option>
                {workflows.map((wf) => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name}
                    {wf.category ? ` (${wf.category})` : ''}
                  </option>
                ))}
              </select>
              {workflows.length === 0 && <p className="text-xs text-glass-500 mt-1">No active workflows.</p>}
            </div>

            <div>
              <label className="text-sm text-glass-300 mb-1 block">Default assignee (optional)</label>
              <select
                value={assigneeUuid}
                onChange={(e) => setAssigneeUuid(e.target.value)}
                className="glass-input w-full text-sm"
              >
                <option value="">Leave tasks unassigned</option>
                {members.map((m) => (
                  <option key={m.minecraft_uuid} value={m.minecraft_uuid}>
                    {m.display_name}
                  </option>
                ))}
              </select>
            </div>

            {loadingDetail && (
              <div className="flex items-center gap-2 text-glass-400 text-sm">
                <Loader className="w-4 h-4 animate-spin" /> Loading stages…
              </div>
            )}

            {selected && !loadingDetail && (
              <div>
                <h3 className="text-sm font-semibold text-glass-300 mb-2 inline-flex items-center gap-1.5">
                  <CalendarClock className="w-4 h-4 text-cyan-400" />
                  Due date preview
                </h3>
                {scheduledStart == null && (
                  <p className="text-xs text-amber-300 mb-2">No scheduled start. Tasks will have no due dates.</p>
                )}
                <div className="space-y-1">
                  {orderedStages.map((stage, i) => {
                    const due = computeStageDueDate(scheduledStart, stage.offset_days);
                    return (
                      <div
                        key={stage.id}
                        className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg bg-glass-100/5 border border-glass-200/10 text-sm"
                      >
                        <span className="text-glass-200 min-w-0 truncate">
                          <span className="text-glass-500 mr-1.5">{i + 1}.</span>
                          {stage.title}
                          {stage.default_role && (
                            <span className="ml-1.5 text-[10px] text-accent-300 capitalize">({stage.default_role})</span>
                          )}
                        </span>
                        <span className="text-xs text-glass-400 flex-shrink-0">
                          {stage.offset_days == null
                            ? 'No due date'
                            : due != null
                              ? `${format(new Date(due), 'MMM d, HH:mm')} (${stage.offset_days >= 0 ? '+' : ''}${stage.offset_days}d)`
                              : `${stage.offset_days >= 0 ? '+' : ''}${stage.offset_days}d from start`}
                        </span>
                      </div>
                    );
                  })}
                  {orderedStages.length === 0 && (
                    <p className="text-xs text-glass-500">This workflow has no stages.</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleApply()}
                disabled={submitting || !selectedId || orderedStages.length === 0}
                className="glass-button text-sm inline-flex items-center gap-1.5"
              >
                {submitting && <Loader className="w-4 h-4 animate-spin" />}
                Apply ({orderedStages.length} task{orderedStages.length === 1 ? '' : 's'})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
